import { useSelector } from 'react-redux';
import { useCallback, useContext, useEffect, useRef } from 'react';
import type { RootState } from '../grid/reduxStore/Store';
import { useAppDispatch } from '../grid/reduxStore/Store';
import { useCurrentUser } from '../../../providers/UserProvider';
import { SelectionContext } from '../GridDndEditor/SelectedBlockInfoProvider';
import type { BlockSettings, ShapeType } from '../grid/reduxStore/blockStyleSettingsSlice';
import {
  loadBlockStyleSettings,
  addBlockSettings,
  deleteBlockSettings,
  updateBlockSettings,
  setActiveSidePanel,
  EditorDefaultSidePanelType,
} from '../grid/reduxStore/blockStyleSettingsSlice';
import { DEFAULT_SHAPE_BACKGROUND_COLOR } from '../shared/documentColorDefaults';
import type { SectionContentType } from '../Sections/Section.model';
import { BlockSides } from '../GridDndEditor/Block/Helper/BlockSides';
import type { ColorResult } from '../shared/components/ColorPicker';
import { useBlockDesignHandlerWithoutUndoRedo, useBlockSettingsHandler } from './useBlockSettingsHandler';
import { DEFAULT_BLOCK_BACKGROUND_COLOR, DEFAULT_BLOCK_BORDER_COLOR } from '../shared/documentColorDefaults';

const initialBorderSettings = {
  borderLeft: 0,
  borderRight: 0,
  borderTop: 0,
  borderBottom: 0,
};

export const defaultBlockStyle: BlockSettings = {
  blockId: '',
  updatedByUserId: 0,
  lockedBy: null,
  borderRadius: 0,
  borderColor: DEFAULT_BLOCK_BORDER_COLOR,
  backgroundColor: DEFAULT_BLOCK_BACKGROUND_COLOR,
  opacity: 100,
  imageAlt: '',
  imageLink: '',
  borderWidth: 0,
  currentBorderSide: [BlockSides.ALL_SIDES],
  paddingLeft: 0,
  paddingRight: 0,
  paddingTop: 0,
  paddingBottom: 0,
  imageAspectRatioLock: true,
  ...initialBorderSettings,
};

export const useBlockStyleSettings = () => {
  const { data: currentUser } = useCurrentUser();
  const userId = currentUser.id;
  const originalStateRef = useRef<BlockSettings>();

  const initialBlockStyle = { ...defaultBlockStyle, updatedByUserId: userId };
  const { selectedBlockIdByWrapper } = useContext(SelectionContext);
  const blockId = selectedBlockIdByWrapper as string;

  const { changeBlockDesign } = useBlockSettingsHandler();
  const blockDesignHandlerWithoutUndoRedo = useBlockDesignHandlerWithoutUndoRedo();
  const dispatch = useAppDispatch();
  const blocksSettings = useSelector((state: RootState) => state.blockStyleSettings);

  const getBlockStyle = useCallback(() => {
    if (blocksSettings.blockStyle[blockId]) {
      return blocksSettings.blockStyle[blockId];
    }
    return { ...initialBlockStyle };
  }, [blocksSettings.blockStyle, blockId, initialBlockStyle]);

  useEffect(() => {
    !blockId && dispatch(setActiveSidePanel({ type: EditorDefaultSidePanelType.EDITOR_DEFAULT_SIDE_PANEL }));
  }, [blockId, dispatch]);

  const getActiveBorderSides = (localStyle: BlockSettings, borderWidth: number) => {
    const blockSettings = { ...localStyle };
    const activeBorderSides = blockSettings.currentBorderSide ?? [BlockSides.ALL_SIDES];
    if (activeBorderSides.includes(BlockSides.LEFT) || activeBorderSides.includes(BlockSides.ALL_SIDES)) {
      blockSettings.borderLeft = borderWidth;
    }
    if (activeBorderSides.includes(BlockSides.RIGHT) || activeBorderSides.includes(BlockSides.ALL_SIDES)) {
      blockSettings.borderRight = borderWidth;
    }
    if (activeBorderSides.includes(BlockSides.TOP) || activeBorderSides.includes(BlockSides.ALL_SIDES)) {
      blockSettings.borderTop = borderWidth;
    }
    if (activeBorderSides.includes(BlockSides.BOTTOM) || activeBorderSides.includes(BlockSides.ALL_SIDES)) {
      blockSettings.borderBottom = borderWidth;
    }
    return { ...blockSettings, borderWidth };
  };

  const displayChange = (blockId, { opacity, imageAlt, imageLink }) => {
    if (!blockId) return;

    const displayStyles = { opacity, imageAlt, imageLink };
    const beforeStyle = getBlockStyle();
    const updatedStyles = {
      ...beforeStyle,
      ...displayStyles,
    };

    changeBlockDesign(blockId, originalStateRef.current || beforeStyle, updatedStyles);
    originalStateRef.current = undefined;
  };

  const loadBlockStyles = (sections: SectionContentType[]) => {
    dispatch(loadBlockStyleSettings({ sections, defaultBlockStyle }));
  };

  const addBlock = (blockId: string | null, isShapeBlockType?: boolean) => {
    if (blockId && !blocksSettings.blockStyle[blockId]) {
      const defaultAddedBlockSettings = {
        ...defaultBlockStyle,
        blockId,
      };
      // Keep in sync with shapeDefaultStyle in hooks/useNewBlockSettings.ts.
      const shapeAddedBlockSettings = {
        ...defaultAddedBlockSettings,
        backgroundColor: DEFAULT_SHAPE_BACKGROUND_COLOR,
        imageAspectRatioLock: false,
      };
      const addedBlockSettings = isShapeBlockType ? shapeAddedBlockSettings : defaultAddedBlockSettings;
      const payload = { blockId: blockId || '', blockSettings: addedBlockSettings };
      dispatch(addBlockSettings(payload));
    }
  };

  const deleteBlock = (blockId: string | null) => {
    blockId && dispatch(deleteBlockSettings({ blockId }));
  };

  const updateBlockStyle = (blockId: string | null, blockSettings) => {
    blockId && dispatch(updateBlockSettings({ blockId, blockSettings }));
  };

  const changeBlockBackgroundColor = ({ color, canBeUndo = true }: { color: ColorResult; canBeUndo?: boolean }) => {
    const payload = { blockId: blockId, backgroundColor: color };

    if (blockId) {
      const localStyle = getBlockStyle();

      const beforeState = { ...localStyle };
      const afterState = { ...localStyle, ...payload };

      if (!canBeUndo) {
        return blockDesignHandlerWithoutUndoRedo(blockId, beforeState, afterState);
      }
      changeBlockDesign(blockId, originalStateRef.current || beforeState, afterState);
      originalStateRef.current = undefined;
    }
  };

  const changeBlockBorderColor = ({ color, canBeUndo = true }: { color: ColorResult; canBeUndo?: boolean }) => {
    const payload = { blockId: blockId, borderColor: color };

    if (blockId) {
      const localStyle = getBlockStyle();

      const beforeState = { ...localStyle };
      const afterState = { ...localStyle, ...payload };

      if (!canBeUndo) {
        return blockDesignHandlerWithoutUndoRedo(blockId, beforeState, afterState);
      }
      changeBlockDesign(blockId, originalStateRef.current || beforeState, afterState);
      originalStateRef.current = undefined;
    }
  };

  const changeBlockBorderSide = (sides: BlockSides[]) => {
    if (!blockId) return;

    let updatedCurrentBlockStyle: BlockSettings = { ...blocksSettings.blockStyle[blockId] };
    const { borderWidth } = updatedCurrentBlockStyle;
    const activeBorderSettings = {};

    const beforeState = { ...updatedCurrentBlockStyle };

    if (!sides.length) {
      updatedCurrentBlockStyle = { ...updatedCurrentBlockStyle, ...initialBorderSettings };
    } else {
      sides.forEach((side: BlockSides) => {
        const borderName = `border${side}`;
        activeBorderSettings[borderName] = borderWidth;
      });
      updatedCurrentBlockStyle = { ...getBlockStyle(), ...initialBorderSettings, ...activeBorderSettings };
    }
    updatedCurrentBlockStyle.currentBorderSide = sides;

    changeBlockDesign(blockId, originalStateRef.current || beforeState, updatedCurrentBlockStyle);
    originalStateRef.current = undefined;
  };

  const changeBorderRadius = ({ radius, toAddPadding = false }: { radius: number; toAddPadding?: boolean }) => {
    if (blockId) {
      const localStyle = getBlockStyle();
      const calculatedPadding = toAddPadding ? radius / 20 : 0;
      const updatedPaddings = {
        paddingLeft: calculatedPadding,
        paddingTop: calculatedPadding,
        paddingRight: calculatedPadding,
        paddingBottom: calculatedPadding,
      };
      const beforeState = { ...localStyle };
      const afterState = {
        ...localStyle,
        borderRadius: radius,
        ...updatedPaddings,
      };
      changeBlockDesign(blockId, originalStateRef.current || beforeState, afterState);
      originalStateRef.current = undefined;
    }
  };

  const changeShapeType = (type: ShapeType, isBorderRadiusShouldBeReset: boolean) => {
    if (blockId) {
      const localStyle = getBlockStyle();
      const beforeState = { ...localStyle };
      const afterState = { ...localStyle, borderRadius: isBorderRadiusShouldBeReset ? 0 : localStyle.borderRadius };

      changeBlockDesign(blockId, originalStateRef.current || beforeState, afterState);
      originalStateRef.current = undefined;
    }
  };

  const changeBorderWidth = (width: number) => {
    const updatedStyle: Partial<BlockSettings> = getActiveBorderSides(getBlockStyle(), width);
    if (blockId) {
      const localStyle = getBlockStyle();
      const beforeState = { ...localStyle };
      const afterState = { ...localStyle, ...updatedStyle };

      changeBlockDesign(blockId, originalStateRef.current || beforeState, afterState);
      originalStateRef.current = undefined;
    }
  };

  const changePadding = (blockId: string | null, paddingStyle) => {
    if (blockId) {
      const localStyle = getBlockStyle();
      const beforeState = { ...localStyle };
      const afterState = { ...localStyle, ...paddingStyle };

      changeBlockDesign(blockId, originalStateRef.current || beforeState, afterState);
      originalStateRef.current = undefined;
    }
  };

  const changeAspectRatioLock = (isAspectRatioLock: boolean) => {
    const payload = { blockId: blockId || '', imageAspectRatioLock: isAspectRatioLock };
    if (blockId) {
      const localStyle = getBlockStyle();
      const beforeState = { ...localStyle };
      const afterState = { ...localStyle, ...payload };

      changeBlockDesign(blockId, originalStateRef.current || beforeState, afterState);
      originalStateRef.current = undefined;
    }
  };

  const saveCurrentBlockStyle = (style: BlockSettings) => {
    const currentStyle = getBlockStyle();
    changeBlockDesign(style.blockId, currentStyle, style);
  };

  const saveCopiedBlockStyle = async (blockId: string, style: BlockSettings) => {
    return await changeBlockDesign(blockId, null, style);
  };

  const saveCurrentStateToRef = (style: BlockSettings) => {
    originalStateRef.current = { ...style };
  };

  const getBlockStyleById = (blockId: string): BlockSettings | undefined => {
    return blocksSettings.blockStyle[blockId];
  };

  return {
    getBlockStyle,
    getBlockStyleById,
    loadBlockStyles,
    addBlock,
    deleteBlock,
    updateBlockStyle,
    changeBlockBackgroundColor,
    changeBlockBorderColor,
    changeBlockBorderSide,
    changeBorderRadius,
    changeBorderWidth,
    changePadding,
    changeAspectRatioLock,
    displayChange,
    saveCurrentBlockStyle,
    saveCopiedBlockStyle,
    changeShapeType,
    saveCurrentStateToRef,
    blockDesignHandlerWithoutUndoRedo,
  };
};
